import React, { useState } from 'react';
import { Check, Crown, Zap, Scale, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../redux/store';
import { createPaymentLinkThunk, fetchAvailablePlansThunk } from '../../redux/thunks/SubscriptionThunks';
import { useEffect } from 'react';

interface PricingPlansProps {
  userId?: string;
  userEmail?: string;
}

const PLANS = [
  {
    key: 'practitioner',
    name: 'Practitioner',
    price: '€150',
    renewPrice: '€300',
    period: 'month',
    icon: <Zap className="h-6 w-6 text-gold/70" />,
    recommended: false,
    features: [
      '100 research queries per month',
      '1 user licence',
      'EUR-Lex, Cyprus & Austria databases',
      'Citation-backed answers',
      'Case-law search',
      'Email support',
    ],
    cta: 'Start Free Trial',
    ctaVariant: 'outline' as const,
  },
  {
    key: 'professional',
    name: 'Professional',
    price: '€250',
    renewPrice: '€500',
    period: 'month',
    icon: <Crown className="h-6 w-6 text-gold" />,
    recommended: true,
    features: [
      'Unlimited research queries',
      'Up to 3 user licences',
      'Case management',
      'Document upload & analysis',
      'Future jurisdictions included',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    ctaVariant: 'default' as const,
  },
  {
    key: 'custom',
    name: 'Custom',
    price: "Let's talk",
    renewPrice: null,
    period: null,
    icon: <Scale className="h-6 w-6 text-gold/80" />,
    recommended: false,
    features: [
      'Unlimited user licences',
      'Everything in Professional',
      'SSO & advanced security',
      'Custom data sources',
      'Dedicated onboarding',
      'Named account manager',
    ],
    cta: 'Contact us',
    ctaVariant: 'outline' as const,
  },
];

const PricingPlans: React.FC<PricingPlansProps> = ({ userId, userEmail }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [planIds, setPlanIds] = useState<Record<string, string>>({});
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAvailablePlansThunk())
      .unwrap()
      .then((plans: any[]) => {
        const map: Record<string, string> = {};
        plans.forEach(p => {
          const name = (p.name || '').toLowerCase();
          if (name.includes('practitioner') || name.includes('starter') || name.includes('basic')) map['practitioner'] = p.id;
          else if (name.includes('professional') || name.includes('pro') || name.includes('premium')) map['professional'] = p.id;
        });
        setPlanIds(map);
      })
      .catch(() => {});
  }, [dispatch]);

  const handleSelectPlan = async (planKey: string) => {
    if (planKey === 'custom') {
      window.location.href = 'mailto:omnilaw.ai@gmail.com';
      return;
    }
    const priceId = planIds[planKey];
    if (!priceId) return;
    setLoadingKey(planKey);
    setPaymentError(null);
    try {
      const currentUrl = window.location.origin;
      await dispatch(createPaymentLinkThunk({
        priceId,
        successUrl: `${currentUrl}/account-settings?subscription=success`,
        cancelUrl: `${currentUrl}/account-settings?subscription=cancelled`,
      })).unwrap();
    } catch (err: any) {
      console.error('Payment link error:', err);
      setPaymentError(err?.message || 'Failed to create payment link. Please try again.');
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="py-4">
      {/* Payment error */}
      {paymentError && (
        <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive text-sm">
          <span className="shrink-0 mt-0.5">⚠</span>
          {paymentError}
        </div>
      )}

      {/* Promo banner */}
      <div className="mb-6 px-4 py-3 rounded-xl bg-gold/10 border border-gold/20 text-center">
        <p className="text-sm font-semibold text-gold">
          🎁 50% off your first year — introductory pricing for new firms · limited time
        </p>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((plan) => (
          <div key={plan.key} className="relative">
            {plan.recommended && (
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                <Badge className="bg-gold text-white px-3 py-0.5 text-[10px] font-black tracking-widest uppercase shadow-md">
                  Recommended
                </Badge>
              </div>
            )}

            <div className={`flex flex-col h-full rounded-2xl border-2 p-5 transition-all ${
              plan.recommended
                ? 'border-gold bg-gradient-to-b from-gold/5 to-transparent shadow-lg shadow-gold/10'
                : 'border-border bg-card hover:border-gold/30 hover:shadow-md'
            }`}>
              {/* Plan icon + name */}
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                  {plan.icon}
                </div>
                <h3 className="text-base font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                  {plan.name}
                </h3>
              </div>

              {/* Price */}
              <div className="mb-4">
                {plan.period ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                        {plan.price}
                      </span>
                      <span className="text-sm text-muted-foreground">/{plan.period}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      First year · renews at {plan.renewPrice}/month
                    </p>
                  </>
                ) : (
                  <div className="text-2xl font-bold text-gold" style={{ fontFamily: 'var(--font-display)' }}>
                    {plan.price}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-border mb-4" />

              {/* Features */}
              <ul className="flex-1 space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <div className="mt-0.5 w-4 h-4 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                      <Check className="h-2.5 w-2.5 text-gold" strokeWidth={3} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                onClick={() => handleSelectPlan(plan.key)}
                disabled={loadingKey !== null || (plan.key !== 'custom' && !planIds[plan.key])}
                variant={plan.recommended ? 'default' : 'outline'}
                className={`w-full font-semibold gap-2 ${
                  plan.recommended
                    ? 'bg-gold hover:bg-gold-bright text-white border-none shadow-md'
                    : 'border-gold/30 text-foreground hover:border-gold hover:text-gold'
                }`}
              >
                {loadingKey === plan.key ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</>
                ) : plan.key === 'custom' ? (
                  <><MessageSquare className="h-4 w-4" />{plan.cta}</>
                ) : (
                  plan.cta
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-6 text-center text-xs text-muted-foreground space-y-1">
        <p>Cancel anytime · 30-day money-back guarantee · 24/7 support</p>
        <a href="mailto:omnilaw.ai@gmail.com" className="text-gold hover:text-gold-bright font-medium">
          Need a custom plan? Contact us →
        </a>
      </div>
    </div>
  );
};

export default PricingPlans;
